import { useEffect } from 'react';

import { useTomatoHuntStore } from '../store/tomatoHunt';

/**
 * 오늘 토마토 찾기 — 숨은 아이콘.
 * 서버가 정한 오늘의 정답 위치(elementId)와 일치하는 자리에서만 렌더된다. 못 찾은 동안만 뜨고,
 * 찾으면(스토어 found=true) 사라진다. 작게·반투명으로 "숨은" 느낌을 준다.
 *
 * 배치 규칙: 반드시 다른 클릭 요소(<Link>/<button>)의 **형제**로 둔다(자식 X). 안에 넣으면
 * HTML상 중첩 인터랙션이 되고 부모 클릭도 같이 튄다. 글 카드에선 relative 래퍼 + absolute로 띄운다.
 */
export function TomatoHuntIcon({ elementId, className }: { elementId: string; className?: string }) {
  const target = useTomatoHuntStore((s) => s.targetElementId);
  const claiming = useTomatoHuntStore((s) => s.claiming);
  const claim = useTomatoHuntStore((s) => s.claim);

  if (target !== elementId) return null;

  return (
    <button
      type="button"
      aria-label="오늘의 토마토"
      title="오늘의 토마토?"
      disabled={claiming}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        void claim(elementId);
      }}
      className={`inline-flex items-center justify-center leading-none drop-shadow-md transition hover:scale-110 active:scale-90 ${
        className ?? ''
      }`}
    >
      {/* 이모지에만 꿈틀 모션 — 부모 버튼의 hover 스케일과 transform이 겹치지 않게 분리. */}
      <span className="tomato-wiggle text-[34px]">🍅</span>
    </button>
  );
}

/**
 * 찾은 직후 뜨는 축하 토스트. 몇 초 뒤 자동으로 사라지고, 눌러도 닫힌다.
 * "오늘 N명이 찾았어요"로 사회적 증거를 함께 보여준다.
 */
export function TomatoHuntCelebration() {
  const celebration = useTomatoHuntStore((s) => s.celebration);
  const dismiss = useTomatoHuntStore((s) => s.dismissCelebration);

  useEffect(() => {
    if (!celebration) return;
    const timer = setTimeout(dismiss, 4000);
    return () => clearTimeout(timer);
  }, [celebration, dismiss]);

  if (!celebration) return null;

  return (
    <div
      // 모바일에선 하단 탭바(+수확철 바) 위로 띄운다.
      className="fixed inset-x-0 bottom-6 z-50 flex justify-center px-5 max-sm:bottom-[calc(5rem+env(safe-area-inset-bottom))]"
      onClick={dismiss}
      role="status"
    >
      <div className="pointer-events-none rounded-2xl bg-ink-900/95 px-5 py-3.5 text-center text-ink-50 shadow-xl">
        <p className="text-[14px] font-semibold">
          {celebration.allFound ? '오늘의 토마토를 다 찾았어요! 🎉' : '오늘의 토마토를 찾았습니다! 🍅'}
        </p>
        <p className="mt-0.5 text-[12px] text-ink-50/70">
          {celebration.allFound ? '다 찾음' : `${celebration.remaining}개 남음`} · +
          {celebration.reward}🍅 · 오늘 {celebration.finderCount}명이 찾았어요
        </p>
      </div>
    </div>
  );
}
